/* eslint-disable complexity */
import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';

import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import { Slide, useTheme } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import {
  useFilesService,
  useImageConverter,
  useImageRotator,
} from '../../services/hooks';
import { useIsDesktop } from '../../hooks';
import { Icon } from '../../Icon';
import { Link } from '../../Link';
import { Tooltip } from '../../Tooltip';
import { Button } from '../../Button';
import { FilePreview } from '../../FilePreview';
import { Box } from '../../Box';
import { Grid } from '../../Grid';
import { Paper } from '../../Paper';
import { CircularProgress } from '../../CircularProgress';
import { Typography } from '../../Typography';
import { FileError, Label, HelperText } from '../common';

import { FillBanner } from './FillBanner';
import { FileInputValue } from './FileInput.intefaces';
import useStyles from './FileInput.styles';

export type ValidateType = (
  value: unknown,
  allValues?: unknown,
) => (string | undefined) | Promise<string | undefined>;

const composeValidators =
  (validators: ValidateType[]) =>
  async (value: unknown, allValues?: unknown) => {
    for (const validator of validators) {
      const error = await validator?.(value, allValues);
      if (error) return error;
    }
    return undefined;
  };

export interface FileInputProps {
  value: FileInputValue | undefined;
  onChange: (value: FileInputValue | undefined) => void;
  onBlur?: () => void;
  label: string;
  tooltip?: React.ReactNode;
  variant?: 'input' | 'preview';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: JSX.Element;
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;
  fullWidth?: boolean;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  onCacheSuccess?: (cacheId: string) => void;
  onCacheError?: () => void;
  bannerText?: string;
  autorecognitionQr?: boolean;
  testId?: string;
  withAuth?: boolean;
  withZoom?: boolean;
  validators?: ReturnType<typeof composeValidators>;
  allowedTypes?: string[];
}

/**
 * Компонент FileInput представляет собой пользовательский интерфейс для загрузки файлов.
 * Он поддерживает различные варианты отображения (input и dropzone) и предоставляет функциональность
 * для предварительного просмотра, удаления и поворота изображений.
 *
 * @template T - Тип данных, используемых для значения компонента.
 * @param {string} [label] - Текст метки для компонента.
 * @param {boolean} [required] - Флаг, указывающий, является ли поле обязательным.
 * @param {string} [tooltip] - Текст тултипа для метки.
 * @param {boolean} [isLoading] - Флаг, указывающий на состояние загрузки.
 * @param {boolean} [disabled] - Флаг, указывающий на отключенное состояние компонента.
 * @param {string} [cacheFileName] - Имя файла, используемое из кеша.
 * @param {T} [value] - Текущее значение компонента.
 * @param {(filter: Filter) => Promise<T[]>} [requestItems] - Функция для выполнения запроса за элементами.
 * @param {() => void} [resetItems] - Функция для сброса состояния.
 * @param {string} [placeholder] - Текст плейсхолдера.
 * @param {string} [helperText] - Дополнительный текст подсказки.
 * @param {string} [errorText] - Текст ошибки.
 * @param {string} [bannerText] - Текст баннера.
 * @param {number} [previewWidth] - Ширина предварительного просмотра файла.
 * @param {number} [previewHeight] - Высота предварительного просмотра файла.
 * @param {number} [previewResponsiveHeight] - Отзывчивая высота предварительного просмотра файла.
 * @param {boolean} [fullWidth] - Флаг, указывающий на использование всей доступной ширины.
 * @param {(file: File) => void} [load] - Функция для загрузки файла.
 * @param {() => void} [removeDocument] - Функция для удаления документа.
 * @param {() => void} [rotateDocument] - Функция для поворота документа.
 * @param {string[]} [allowedTypes] - Допустимые типы файлов.
 * @param {Blob | null} [blobDocument] - Blob данных загруженного документа.
 * @param {string} [testId] - Тестовый идентификатор для компонента.
 * @param {string | number} [variant] - Вариант отображения компонента (input или dropzone).
 * @param {boolean} [error] - Флаг, указывающий на наличие ошибки.
 * @param {boolean} [withZoom] - Флаг для масштабирования документа.
 * @param {Object} [rest] - Дополнительные пропсы для передачи в компонент.
 * @param {boolean} [withAuth] - Флаг, указывающий необходимо ли передавать токен авторизации.
 *
 * @returns {JSX.Element} Компонент FileInput.
 *
 */

const ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/apng',
  'image/gif',
  'image/webp',
  'image/avif',
];

export const FileInput = ({
  value,
  onChange,
  onBlur = () => {},
  label,
  tooltip,
  variant = 'input',
  placeholder = 'Перенесите файл, что бы прикрепить документ',
  required = false,
  disabled = false,
  loading = false,
  loadingText = undefined,
  cache,
  fullWidth,
  error = false,
  errorText = '',
  helperText = '',
  onCacheSuccess = () => {},
  onCacheError = () => {},
  bannerText,
  autorecognitionQr = false,
  testId = 'test-id-file-input',
  withAuth,
  withZoom = false,
  validators,
  allowedTypes = ALLOWED_TYPES,
  ...rest
}: FileInputProps) => {
  const { classes } = useStyles();
  const isDesktop = useIsDesktop();
  const filesService = useFilesService();
  const imageRotator = useImageRotator();
  const imageConverter = useImageConverter();
  const previewBlock = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const [isFileLoading, setIsFileLoading] = useState(loading);
  const [isButtonsVisible, setIsButtonsVisible] = useState(false);
  const [cacheFileName, setCacheFilename] = useState('');
  const [blobDocument, setBlobDocument] = useState<Blob | null>(null);
  const [validationErrorMessage, setValidationErrorMessage] =
    useState<string>('');
  const [invalidFileName, setInvalidFileName] = useState<string>('');

  const finalError = error || validationErrorMessage;

  const isLoading = loading || isFileLoading;
  const isDisabled = disabled || isLoading;

  const previewWidth = 296;
  let previewHeight;

  if (isDesktop || autorecognitionQr || (value && !error && !isLoading)) {
    previewHeight = 418;
  } else if (bannerText && !value) {
    previewHeight = 144;
  } else {
    previewHeight = 92;
  }

  const previewBlockRect = previewBlock?.current?.getBoundingClientRect();
  const previewBlockWidth = previewBlockRect?.width ?? previewWidth;

  const previewResponsiveHeight =
    (previewHeight * previewBlockWidth) / previewWidth;

  /**
   * Функция handleChange обрабатывает изменение значения компонента FileInput.
   * Она вызывает переданную функцию onChange с новым значением.
   * Если ранее было установлено cacheFileName, оно сбрасывается.
   *
   * @param {FileInputValue | undefined} nextValue - Новое значение компонента или undefined.
   * @returns {void}
   */
  const handleChange = useCallback(
    (nextValue: FileInputValue | undefined) => {
      if (cacheFileName) setCacheFilename('');

      onChange(nextValue);
    },
    [onChange, cacheFileName],
  );

  useEffect(() => {
    setBlobDocument(null);
  }, [value]);

  /**
   * Функция load загружает документ в формате Blob.
   * Если документ уже загружен (blobDocument не null), возвращает его.
   * Если источник документа (value?.source) - строка, загружает файл из сервиса FilesService.
   * Устанавливает имя файла в кэше (cacheFileName) и документ (blobDocument).
   * Если источник документа - объект типа Blob, устанавливает его как blobDocument.
   * Возвращает загруженный или ранее установленный документ.
   *
   * @returns {Promise<Blob>} - Обещание, которое разрешится в Blob документе.
   */
  const load: () => Promise<Blob> = useCallback(async () => {
    if (blobDocument) {
      return blobDocument;
    }

    if (typeof value?.source === 'string') {
      const fileData = await filesService.getFile(value.source, withAuth);
      const { file, filename } = await fileData;

      if (filename) {
        setCacheFilename(filename);
      }

      setBlobDocument(await file);
      return file;
    } else {
      if (value?.source) {
        setBlobDocument(value.source);
      }

      return value?.source || new Blob();
    }
  }, [filesService, value?.source, blobDocument, withAuth]);

  const isHEICFile = (file: File) => {
    const fileName = file.name.toLowerCase();

    const isHEICByExtension =
      fileName.endsWith('.heic') || fileName.endsWith('.heif');

    const isHEICByType = [
      'image/heic',
      'image/heif',
      'image/heif-sequence',
      'image/heic-sequence',
    ].includes(file.type);

    const isHEICOnWindows =
      (file.type === '' || file.type === 'application/octet-stream') &&
      isHEICByExtension;

    return isHEICByExtension || isHEICByType || isHEICOnWindows;
  };

  /**
   * Функция onDrop обрабатывает перетаскивание файлов.
   * Если файлы перетащены, устанавливает флаг загрузки (isFileLoading) в true.
   * Проверяет формат загруженного файла на соответствие разрешённым типам.
   * Если использование кэша (cache) разрешено и формат файла неподдерживается,
   * конвертирует файл с помощью ImageConverter и кэширует его.
   * Устанавливает конвертированный или исходный файл как blobDocument
   * и вызывает handleChange с новым значением.
   * Если формат файла поддерживается и кэш разрешён, кэширует файл и вызывает handleChange.
   * Если использование кэша запрещено, конвертирует файл (если формат неподдерживается)
   * и вызывает handleChange с новым значением.
   * Устанавливает флаг загрузки (isFileLoading) в false.
   * Если файлы не перетащены, вызывает handleChange с undefined.
   * Снимает фокус с активного элемента и вызывает onBlur.
   *
   * @param {File[]} files - Массив загруженных файлов.
   * @returns {void}
   */
  const onDrop = async (files: File[]) => {
    if (files && files.length) {
      setIsFileLoading(true);
      setValidationErrorMessage('');
      setInvalidFileName('');

      const uploadedFile = files[0];

      const isWrongFormat =
        isHEICFile(uploadedFile) ||
        ['image/tiff', 'image/dng', 'image/x-adobe-dng'].includes(
          uploadedFile.type,
        );

      if (validators && !isWrongFormat) {
        try {
          const validationResult = await validators({ source: uploadedFile });

          if (validationResult) {
            setValidationErrorMessage(validationResult);
            setInvalidFileName(uploadedFile.name);
            setIsFileLoading(false);
            return;
          } else {
            setValidationErrorMessage('');
            setInvalidFileName('');
          }
        } catch (validationError) {
          setValidationErrorMessage('Ошибка при проверке файла');
          setInvalidFileName(uploadedFile.name);
          setIsFileLoading(false);
          return;
        }
      }

      if (cache) {
        if (isWrongFormat) {
          const [fileCacheResponse, blob] = await Promise.all([
            cache(uploadedFile).catch(() => null),

            imageConverter.convert(uploadedFile).catch(() => null),
          ]);

          const cacheId = fileCacheResponse?.file_id;
          const cacheExpiredAt = fileCacheResponse?.expired_at;

          const convertedFile =
            blob &&
            new File([blob], 'image.jpeg', {
              type: blob.type,
            });

          setBlobDocument(convertedFile || new Blob());

          handleChange({
            value: cacheId
              ? JSON.stringify({
                  type: 'file_cache',
                  id: cacheId,
                  expiredAt: cacheExpiredAt || '',
                })
              : convertedFile || uploadedFile,
            source: convertedFile || uploadedFile,
          });
        } else {
          try {
            const { file_id: cacheId, expired_at: cacheExpiredAt } =
              await cache(uploadedFile);

            setBlobDocument(uploadedFile);
            handleChange({
              value: JSON.stringify({
                type: 'file_cache',
                id: cacheId,
                expiredAt: cacheExpiredAt,
              }),
              source: uploadedFile,
            });
            onCacheSuccess(cacheId.toString());
          } catch (e) {
            onCacheError();
          }
        }
      } else if (!cache) {
        if (isWrongFormat) {
          const blob = await imageConverter.convert(uploadedFile);

          const convertedFile = new File([blob], 'image.jpeg', {
            type: blob.type,
          });

          setBlobDocument(blob);
          handleChange({
            value: convertedFile,
            source: convertedFile,
          });
        } else {
          setBlobDocument(uploadedFile);
          handleChange({
            value: uploadedFile,
            source: uploadedFile,
          });
        }
      }

      setIsFileLoading(false);
    } else {
      handleChange(undefined);
    }

    (document?.activeElement as Element & { blur: () => void })?.blur();
    onBlur();
  };

  const { open, getRootProps, getInputProps } = useDropzone({
    disabled: isDisabled,
    onDrop,
  });

  /**
   * Функция rotateDocument вращает загруженный документ.
   * Сначала загружает Blob документа, затем вызывает imageRotator для вращения.
   * Если вращение успешно, создаёт новый файл с обновлённым Blob и вызывает onDrop с этим файлом.
   *
   * @returns {void}
   */
  const rotateDocument = () => {
    load().then((blob: Blob) => {
      imageRotator.rotate(blob).then((updatedBlob: Blob | null) => {
        if (updatedBlob) {
          const format = updatedBlob.type.split('/').pop();

          const file = new File([updatedBlob], `image.${format}`, {
            type: updatedBlob.type,
          });

          onDrop([file]);
        }
      });
    });
  };

  /**
   * Функция removeDocument удаляет загруженный документ.
   * Вызывает handleChange с undefined для очистки значения и скрывает кнопки управления.
   *
   * @returns {void}
   */
  const removeDocument = () => {
    handleChange(undefined);
    setIsButtonsVisible(false);
  };

  /**
   * Функция createInputText создаёт текст для отображения в поле ввода.
   * В зависимости от состояния загрузки и наличия значения, возвращает соответствующий текст.
   * Если документ загружается, возвращает "Загрузка...".
   * Если значение существует, возвращает имя файла или кэшированное имя файла.
   * Если значение отсутствует, возвращает текст-заполнитель.
   *
   * @returns {string | JSX.Element} - Текст или элемент для отображения в поле ввода.
   */
  const createInputText = useCallback(() => {
    if (isLoading) {
      return 'Загрузка...';
    } else if (value) {
      return (
        (typeof value.source !== 'string' && value.source.name) ||
        (typeof value.value === 'string' && (cacheFileName || label))
      );
    } else if (invalidFileName) {
      return invalidFileName;
    }

    return placeholder;
  }, [cacheFileName, isLoading, label, placeholder, value, invalidFileName]);

  const errorDisplay = useMemo(() => {
    if (validationErrorMessage) {
      return <FileError text={validationErrorMessage} />;
    }
    if (error) {
      return <FileError text={errorText} />;
    }
    if (helperText && !value) {
      return <HelperText text={helperText} />;
    }
    return null;
  }, [error, errorText, validationErrorMessage, helperText, value]);

  const inputLabel = label ? (
    <Label label={label} required={required} tooltip={tooltip} />
  ) : null;

  const inputVariantDropzone = (
    <div
      {...getRootProps({ className: 'dropzone aqa_file_input_dropzone' })}
      data-testid={testId}
    >
      <input {...rest} {...getInputProps()} />
      <div
        className={classNames(
          classes.dropzone,
          (error || validationErrorMessage) && classes.error,
          isDisabled && classes.disabled,
        )}
      >
        <Typography
          variant="body2"
          color={
            validationErrorMessage || value
              ? 'text.light.primary'
              : 'text.light.tertirary'
          }
          className={classes.inputText}
        >
          {createInputText()}
        </Typography>
      </div>
    </div>
  );

  const inputVariantButton = (
    <Button
      variant="secondary"
      size="large"
      onClick={open}
      disabled={disabled}
      icon={
        isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <AttachFileIcon className={classes.icon} />
        )
      }
    />
  );

  return variant === 'input' ? (
    <Box className={classNames(classes.fileInput, 'aqa_file_input')}>
      {fullWidth ? (
        <Box>
          {inputLabel}
          <Box display="flex" gap="20">
            <Box flexGrow={1} maxWidth="calc(100% - 76px)">
              {inputVariantDropzone}
            </Box>
            <Box>{inputVariantButton}</Box>
          </Box>
        </Box>
      ) : (
        <Grid container spacing="20">
          <Grid item xs={9} md={8}>
            {inputLabel}
            {inputVariantDropzone}
          </Grid>
          <Grid item xs={3} md={4}>
            <Box display="flex" height="100%" alignItems="end">
              {inputVariantButton}
            </Box>
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Grid item xs={12} md={12}>
          {errorDisplay}
        </Grid>
      </Grid>
      {value && !finalError && (
        <Box
          mt={{ xs: '8', md: '16' }}
          width={{ xs: 96, md: 144 }}
          height={{ xs: 96, md: 144 }}
        >
          <Box position="relative">
            {!disabled && (
              <Box
                position="absolute"
                top={7}
                right={7}
                zIndex={1}
                width={24}
                height={24}
                overflow="hidden"
                bgcolor="text.dark.primary"
                border={2}
                borderColor="text.dark.primary"
                radius="round"
                onClick={removeDocument}
              >
                <Box
                  position="absolute"
                  width={20}
                  height={20}
                  top={0}
                  left={0}
                >
                  <Icon
                    name="cancel_circle"
                    color="stroke.secondary"
                    size="20"
                  />
                </Box>
              </Box>
            )}
            <Paper>
              <FilePreview
                shape="square"
                onLoad={load}
                clickable
                withZoom={withZoom}
              />
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  ) : (
    <Box width={fullWidth ? '100%' : previewWidth} testId={testId}>
      {label && <Label label={label} required={required} tooltip={tooltip} />}

      <Box position="relative">
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height={418}
            bgcolor="bg.light.primary"
            border={`${theme.tokens.border.m}px dashed`}
            borderColor="stroke.primary"
            radius="l"
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={
                fullWidth && !autorecognitionQr
                  ? previewResponsiveHeight
                  : previewHeight
              }
            >
              {bannerText && !value && <FillBanner>{bannerText}</FillBanner>}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                width={{ xs: 250 }}
                mt={!isDesktop && bannerText && !value ? '8' : '0'}
                gap="8"
              >
                <CircularProgress size={20} />
                {loadingText || (
                  <Box
                    mb={{ xs: '0', md: '12' }}
                    color={
                      isDesktop || autorecognitionQr
                        ? 'text.light.tertirary'
                        : 'original.brand.primary'
                    }
                  >
                    <Typography
                      display="block"
                      variant={
                        isDesktop || autorecognitionQr ? 'body2' : 'caption'
                      }
                      color="inherit"
                      align="center"
                    >
                      Пожалуйста, подождите
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...rest} {...getInputProps()} />
            <div
              ref={previewBlock}
              className={classNames(
                classes.previewDropzone,
                (error || validationErrorMessage) && classes.error,
                isDisabled && classes.disabled,
              )}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={
                  fullWidth && !autorecognitionQr
                    ? previewResponsiveHeight
                    : previewHeight
                }
              >
                {bannerText && !value && <FillBanner>{bannerText}</FillBanner>}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width={{ xs: 154, md: 200 }}
                  mt={!isDesktop && bannerText && !value ? '56' : '0'}
                >
                  <Box
                    mb="4"
                    height={isDesktop || autorecognitionQr ? 56 : 32}
                    width={isDesktop || autorecognitionQr ? 56 : 32}
                    fontSize={{ xs: autorecognitionQr ? 56 : 32, md: 56 }}
                  >
                    <Icon name="vkhrtek_file" size="48" />
                  </Box>
                  <Box
                    mb={{ xs: autorecognitionQr ? '12' : '0', md: '12' }}
                    color={
                      isDesktop || autorecognitionQr
                        ? 'text.light.tertirary'
                        : 'original.brand.primary'
                    }
                  >
                    <Typography
                      display="block"
                      variant={
                        isDesktop || autorecognitionQr ? 'body2' : 'caption'
                      }
                      color="inherit"
                      align="center"
                    >
                      {placeholder || 'Загрузите изображение или PDF файл'}
                    </Typography>
                  </Box>
                  {(isDesktop || autorecognitionQr) && (
                    <Button
                      variant="secondary"
                      size="small"
                      disabled={disabled}
                    >
                      Загрузить
                    </Button>
                  )}
                </Box>
              </Box>
            </div>
          </div>
        )}
        {value && !finalError && !isLoading && (
          <div
            className={classes.filePreview}
            onMouseEnter={() => setIsButtonsVisible(true)}
            onMouseLeave={() => setIsButtonsVisible(false)}
          >
            <Box position="relative" width="100%" height="100%">
              <Box height="100%">
                <FilePreview withFlex onLoad={load} withZoom />
              </Box>
              {!isDesktop && (
                <Box
                  position="absolute"
                  top={16}
                  right={16}
                  zIndex={1}
                  width={24}
                  height={24}
                  overflow="hidden"
                  bgcolor="text.dark.primary"
                  border={2}
                  borderColor="text.dark.primary"
                  radius="round"
                  onClick={removeDocument}
                >
                  <Box
                    position="absolute"
                    width={24}
                    height={24}
                    top={-2}
                    left={-2}
                  >
                    <Icon
                      name="cancel_circle"
                      color="stroke.secondary"
                      size="20"
                    />
                  </Box>
                </Box>
              )}
            </Box>
            {isDesktop && (
              <Slide
                direction="up"
                in={isButtonsVisible}
                mountOnEnter
                unmountOnExit
                timeout={500}
              >
                <div className={classes.previewButtons}>
                  <Box>
                    {blobDocument?.type.startsWith('image/') && (
                      <Tooltip
                        title="Повернуть изображение против часовой стрелки"
                        placement="bottom"
                      >
                        <div>
                          <Link
                            size="medium"
                            onClick={rotateDocument}
                            startIcon={
                              <Icon
                                name="rotate_left"
                                size="20"
                                color="original.brand.primary"
                              />
                            }
                            stroke={false}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </Box>
                  {!disabled && (
                    <Link
                      size="small"
                      onClick={removeDocument}
                      endIcon={
                        <Icon
                          name="delete_outline"
                          size="16"
                          color="original.brand.primary"
                        />
                      }
                      stroke={false}
                    >
                      Удалить
                    </Link>
                  )}
                </div>
              </Slide>
            )}
            {!isDesktop && allowedTypes.includes(blobDocument?.type || '') && (
              <Box position="absolute" right={16} bottom={16}>
                <Button
                  variant="quaternary"
                  icon={
                    <Icon
                      name="rotate_left_outline"
                      size="16"
                      color="original.brand.primary"
                    />
                  }
                  onClick={rotateDocument}
                />
              </Box>
            )}
          </div>
        )}
      </Box>
      {finalError && errorDisplay}
    </Box>
  );
};
